import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import {
  MAT_DIALOG_DATA, MatDialog,
  MatDialogConfig, MatDialogRef
} from '@angular/material/dialog';
import { format, parse } from 'date-fns';
import { finalize } from 'rxjs/operators';

import {
  FollowUpDetailsFragment,
  UpdateFollowUpGQL,
} from 'generated/graphql';

import { MAT_DATE_FNS_MMM_D_Y_FORMATS } from 'core/date/material-datefns-date-adapter';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { ToastrService } from 'core/toastr.service';

export interface FollowUpFlagDueDateDialogData {
  campaignId: string;
  entityName: string;
  followUp: FollowUpDetailsFragment;
}

@Component({
  selector: 'app-follow-up-flag-due-date-dialog',
  templateUrl: './follow-up-flag-due-date.dialog.html',
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_MMM_D_Y_FORMATS }
  ]
})
export class FollowUpFlagDueDateDialog extends WithDisposableSubject() {
  isSaving = false;

  dueDateControl = new FormControl<Date | null>(null);

  formGroup = new FormGroup({
    dueDate: this.dueDateControl,
  });

  get hasChanges() {
    const original = this.data.followUp.dueDate ?? null;
    const current = this.dueDateControl.value
      ? format(this.dueDateControl.value, 'yyyy-MM-dd')
      : null;
    return original !== current;
  }

  onDueDateInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    if (raw.trim() === '') {
      this.dueDateControl.setValue(null);
      this.dueDateControl.setErrors(null);
      this._cd.markForCheck();
    }
  }

  static open(dialog: MatDialog, config: MatDialogConfig<FollowUpFlagDueDateDialogData>) {
    return dialog.open(FollowUpFlagDueDateDialog, config);
  }

  constructor(
    public _dialogRef: MatDialogRef<FollowUpFlagDueDateDialog>,
    private _cd: ChangeDetectorRef,
    private _updateFollowUpGQL: UpdateFollowUpGQL,
    private _toastrService: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: FollowUpFlagDueDateDialogData
  ) {
    super();

    const raw = this.data.followUp.dueDate;
    if (!raw) {
      this.dueDateControl.setValue(null);
    } else {
      this.dueDateControl.setValue(parse(raw, 'yyyy-MM-dd', new Date()));
    }
  }

  onSubmit() {
    if (this.isSaving || !this.hasChanges) { return; }
    this.isSaving = true;

    this._updateFollowUpGQL.mutate({
      input: {
        followUpId: this.data.followUp.id,
        dueDate: this.dueDateControl.value
          ? format(this.dueDateControl.value, 'yyyy-MM-dd')
          : null
      }
    }).pipe(
      finalize(() => {
        this.isSaving = false;
        this._cd.markForCheck();
      })
    ).subscribe({
      next: () => {
        this._toastrService.success('Follow-up due date updated');
        this._dialogRef.close(true);
      },
      error: () => {
        this._toastrService.error('Could not update follow-up due date.');
      }
    });
  }

  close() {
    this._dialogRef.close();
  }

}
